import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { api, money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const Kpi = ({ label, value, hint, accent = "navy", testid }) => (
  <Card className="p-6 border-slate-200 relative overflow-hidden" data-testid={testid || `kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`absolute top-0 left-0 right-0 h-1 ${accent === 'maple' ? 'bg-[#C8102E]' : 'bg-[#0A2540]'}`} />
    <div className="eyebrow text-slate-500">{label}</div>
    <div className="font-mono-num text-3xl font-extrabold text-[#0A2540] mt-3">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
  </Card>
);

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function RevenueChart({ data }) {
  const chartData = (data || []).map((m) => ({
    label: `${MONTH_SHORT[(m.month || 1) - 1]} ${String(m.year).slice(2)}`,
    revenue: Number(m.revenue) || 0,
    count: m.count || 0,
  }));
  const total = chartData.reduce((s, x) => s + x.revenue, 0);
  return (
    <Card className="p-6 border-slate-200" data-testid="revenue-chart-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="eyebrow">Revenue trend</div>
          <h2 className="font-display text-lg font-bold text-[#0A2540]">Monthly brokerage revenue (last 12 months)</h2>
          <div className="text-xs text-slate-500 mt-0.5">Sum of margin on <strong>Completed</strong> transactions, grouped by completion month.</div>
        </div>
        <div className="text-right">
          <div className="eyebrow text-slate-400">12-mo total</div>
          <div className="font-mono-num text-xl font-extrabold text-[#C8102E]" data-testid="revenue-12mo-total">{money(total)}</div>
        </div>
      </div>
      <div className="h-64" data-testid="revenue-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
            <Tooltip
              cursor={{ fill: "#0A254008" }}
              contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
              formatter={(val, _key, item) => [money(val), `Revenue · ${item?.payload?.count || 0} tx`]}
            />
            <Bar dataKey="revenue" fill="#C8102E" radius={[6, 6, 0, 0]} maxBarSize={36} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { can, loaded: permsLoaded } = usePermissions();
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [revenue, setRevenue] = useState(null);

  const isEmployee = user.role === "admin" || user.role === "account_manager";

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([api.get("/dashboard/stats"), api.get("/rfqs")]);
        setStats(s.data);
        setRfqs(r.data.slice(0, 5));
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Phase 3: revenue chart is a permission-gated fetch. Deny-by-default
  // until permissions resolve, then request only if `report.revenue.read`.
  useEffect(() => {
    if (!permsLoaded || !isEmployee) return;
    if (!can("report.revenue.read")) { setRevenue(null); return; }
    (async () => {
      try { const rev = await api.get("/dashboard/revenue-monthly"); setRevenue(rev.data.months || []); }
      catch { setRevenue(null); }
    })();
  }, [permsLoaded, isEmployee, can]);

  if (!stats) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow">Dashboard</div>
        <h1 className="font-display text-3xl font-extrabold text-[#0A2540] tracking-tight mt-1">
          {user.role === "buyer" && `Welcome back, ${user.name.split(" ")[0]}`}
          {user.role === "supplier" && `Hello, ${user.name.split(" ")[0]}`}
          {isEmployee && (user.is_owner ? "Operations Overview" : "My Workspace")}
        </h1>
      </div>

      {user.role === "buyer" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <Kpi label="My RFQs" value={stats.my_rfqs} />
            <Kpi label="Active" value={stats.active_rfqs} hint="Under review · sourcing · quoting" />
            <Kpi label="Pending Selection" value={stats.pending_selection} accent="maple" hint="Quotes awaiting your decision" />
            <Kpi label="Total Spend" value={money(stats.total_spend || 0)} hint="Awarded orders" />
          </div>
          <div className="flex justify-end">
            <Link to="/app/rfqs/new"><Button className="bg-[#0A2540] hover:bg-[#061A2E] gap-2" data-testid="dashboard-new-rfq-btn">Create RFQ <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </>
      )}

      {user.role === "supplier" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <Kpi label="Open Invites" value={stats.open_invites} accent="maple" />
          <Kpi label="Invited RFQs" value={stats.invited_rfqs} />
          <Kpi label="My Quotes" value={stats.my_quotes} />
          <Kpi label="Total Sales" value={money(stats.total_sales || 0)} hint="Awarded contracts" />
        </div>
      )}

      {isEmployee && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <Kpi label="Total RFQs" value={stats.total_rfqs} />
          <Kpi label="Active Pipeline" value={stats.active_rfqs} />
          {/* Phase 3: only render financial KPIs when the backend returned them.
              Backend redacts total_revenue without commercial.margin.read and
              total_supplier_cost without commercial.supplier_cost.read. */}
          {"total_revenue" in stats && (
            <Kpi label="Revenue" value={money(stats.total_revenue)} accent="maple" hint="Sum of margins" testid="kpi-total-revenue" />
          )}
          {"total_volume" in stats && (
            <Kpi label="Total Volume" value={money(stats.total_volume)} hint="Buyer landed totals" testid="kpi-total-volume" />
          )}
          {"total_supplier_cost" in stats && (
            <Kpi label="Supplier Cost" value={money(stats.total_supplier_cost)} hint="Sum of payables" testid="kpi-total-supplier-cost" />
          )}
        </div>
      )}

      {isEmployee && revenue && can("report.revenue.read") && (
        <RevenueChart data={revenue} />
      )}

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Recent RFQs</div>
            <h2 className="font-display text-lg font-bold text-[#0A2540]">Latest activity</h2>
          </div>
          <Link to="/app/rfqs" className="text-sm text-[#1D4ED8] font-medium">View all →</Link>
        </div>
        {rfqs.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No RFQs yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rfqs.map((r) => (
              <Link key={r.id} to={`/app/rfqs/${r.id}`} className="flex items-center justify-between py-3 hover:bg-slate-50 rounded px-2 -mx-2 transition-colors" data-testid={`rfq-row-${r.id}`}>
                <div>
                  <div className="font-medium text-sm text-[#0A2540]">{r.title}</div>
                  <div className="font-mono-num text-xs text-slate-500 mt-0.5">{r.reference} · {r.category}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-slate-500">{r.buyer?.company_name}</span>
                  <span className="text-xs px-2.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 font-medium text-slate-600">{r.status}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
